/**
 * Phase 1: PPC Farm Mail Tracker Cohort Builder Enhancement
 * Service for cohort review operations
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  StartCohortReviewInput, 
  StartCohortReviewResponse,
  COHORT_REVIEW_COLLECTIONS 
} from '../types/ppcFarmTypes';

/**
 * Service class for cohort review operations
 */
class CohortReviewService {
  private startCohortReviewFunction = httpsCallable(functions, 'startCohortReview');
  private getNextCohortNumberFunction = httpsCallable(functions, 'getNextCohortNumberFunction');

  /**
   * Get the next available cohort number
   */
  async getNextCohortNumber(): Promise<number> {
    try {
      console.log('🔢 Fetching next cohort number...');
      
      const result = await this.getNextCohortNumberFunction();
      const data = result.data as any;
      
      if (data.success) {
        console.log(`📊 Next cohort number: ${data.cohortNumber}`);
        return data.cohortNumber;
      } else {
        console.error('❌ Failed to get next cohort number:', data.error);
        throw new Error(data.error || 'Failed to get next cohort number');
      }
    } catch (error) {
      console.error('❌ Error fetching next cohort number:', error);
      throw new Error('Failed to fetch next cohort number');
    }
  }

  /**
   * Start the cohort review process
   */
  async startCohortReview(input: StartCohortReviewInput): Promise<StartCohortReviewResponse> {
    try {
      console.log('🚀 Starting cohort review process...');
      console.log(`👤 Creator: ${input.creatorName}`);
      console.log(`📋 Cohort: ${input.cohortNumber}`);
      console.log(`👥 Sellers: ${input.sellerIds.length}`);
      
      const result = await this.startCohortReviewFunction(input);
      const data = result.data as StartCohortReviewResponse;
      
      if (data.success) {
        console.log('✅ Cohort review started successfully:', data.message);
        return data;
      } else {
        console.error('❌ Failed to start cohort review:', data.error);
        throw new Error(data.error || 'Failed to start cohort review');
      }
    } catch (error) {
      console.error('❌ Error starting cohort review:', error);
      
      // Handle Firebase function errors
      if (error instanceof Error) {
        throw error;
      }
      
      throw new Error('Failed to start cohort review');
    }
  }

  /**
   * Validate cohort review input data
   */
  validateInput(input: Partial<StartCohortReviewInput>): string[] {
    const errors: string[] = [];

    if (!input.creatorName || !input.creatorName.trim()) {
      errors.push('Creator name is required');
    }

    if (!input.cohortNumber || input.cohortNumber < 1) {
      errors.push('Valid cohort number is required');
    }

    if (!input.sellerIds || !Array.isArray(input.sellerIds) || input.sellerIds.length === 0) {
      errors.push('At least one seller is required');
    }

    if (!input.validationResults || !Array.isArray(input.validationResults)) {
      errors.push('Address validation results are required');
    }

    if (input.sellerIds && input.validationResults && 
        input.sellerIds.length !== input.validationResults.length) {
      errors.push('Number of sellers must match number of validation results');
    }

    return errors;
  }

  /**
   * Format validation summary for display
   */
  formatValidationSummary(validationResults: any[]): string {
    if (!validationResults || validationResults.length === 0) {
      return 'No validation results';
    }

    const total = validationResults.length;
    const validated = validationResults.filter(r => r.status === 'VALIDATED').length;
    const unvalidated = validationResults.filter(r => r.status === 'UNVALIDATED').length;
    const errors = validationResults.filter(r => r.status === 'ERROR').length;

    return `${total} addresses processed: ${validated} validated, ${unvalidated} unvalidated, ${errors} errors`;
  }
}

// Export singleton instance
export const cohortReviewService = new CohortReviewService();

// Export types for convenience
export type { StartCohortReviewInput, StartCohortReviewResponse };